import type { Coordinate, DecimalRangeType, StringRangeType } from '../general';
import type { HeartedHotel } from '../semboEndpoints';

import type { SelectedTicketQL } from './graphql_generated/graphql';
import type { TransportVehicleTypeQL } from './responses/itineraryResponse';
import type { AvailabilityOrderQL } from './searchCriterias';

export enum IngredientTypeQL {
  OwnStay = 'OWN_STAY',
  Hotel = 'HOTEL',
  Activity = 'TICKET',
  Flight = 'FLIGHT',
  Ferry = 'FERRY',
  Transport = 'TRANSPORT',
}

export enum TransportTypeQL {
  None = 'NONE',
  Flight = 'FLIGHT',
  Ferry = 'FERRY',
}

export enum SelectedHotelRulesQL {
  FreeCancellation = 'FREE_CANCELLATION',
}

export enum SelectedHotelTypeQL {
  Hotel = 'HOTEL',
  Apartment = 'APARTMENT',
  HouseOrCottage = 'HOUSE_OR_COTTAGE',
  Ticket = 'TICKET',
  Golf = 'GOLF',
}

export enum InputBoardBasisQL {
  RoomOnly = 'ROOM_ONLY',
  Breakfast = 'BREAKFAST',
  HalfBoard = 'HALF_BOARD',
  FullBoard = 'FULL_BOARD',
  AllInclusive = 'ALL_INCLUSIVE',
}

export type IngredientQL =
  | OwnStayIngredientQL
  | HotelIngredientQL
  | ActivityIngredientQL
  | TransportIngredientQL;

export interface OwnStayIngredientQL {
  ingredientType: IngredientTypeQL.OwnStay;
  numberOfNights?: number;
  subIngredients?: IngredientQL[];
}

export interface HotelIngredientQL extends HotelConstraintQL {
  ingredientType: IngredientTypeQL.Hotel;
  numberOfNights?: number;
  numberOfSuggestions?: number;
  heartedHotels?: HeartedHotel[];
  subIngredients?: IngredientQL[];
  suggestedBy?: AvailabilityOrderQL;
  includeAlternativeResults?: boolean;
}

export interface HotelConstraintSelectionQL {
  serializedConstraint?: string;
  destination?: string;
  polygon?: Coordinate[];
  hotelCodes?: string[];
  tagId?: number;
  polygonId?: number;
  destinationCode?: number;
}

export interface HotelConstraintQL extends HotelConstraintSelectionQL {
  starRating?: number;
  freeCancellation?: boolean;
  boardBasis: InputBoardBasisQL[];
  petsAreAllowed?: boolean;
  isBusinessProperty?: boolean;
  isFamilyProperty?: boolean;
  hasParking?: boolean;
  hasPool?: boolean;
  hasSpa?: boolean;
  hasGym?: boolean;
  hasInternet?: boolean;
  hasAirConditioning?: boolean;
  hasElectricVehicleChargingStation?: boolean;
  maxNumberOfHotels?: number;
  selectedHotelTypes: SelectedHotelTypeQL[];
  hotelName?: string;
}

export interface ActivityIngredientQL {
  ingredientType: IngredientTypeQL.Activity;
  code: string;
  offset: number;
  tickets: SelectedTicketQL[];
}

export interface TransportIngredientQL {
  transportTypes: TransportTypeQL[];
  ingredientType: IngredientTypeQL.Transport;
  fromIata: string;
  toIata: string;
  flightNumbers?: string[];
  maxNumberOfStops?: number;
  departureTimeFilters?: StringRangeType;
  arrivalTimeFilters?: StringRangeType;
  maxDurationInHours?: number;
  priceFilters?: DecimalRangeType;
  carriers?: string[];
  vehicles?: TransportVehicleTypeQL[];
  suggestedBy?: AvailabilityOrderQL;
  includeAlternativeResults?: boolean;
}

export interface FerryIngredientQL {
  ingredientType: IngredientTypeQL.Ferry;
  fromIata: string;
  toIata: string;
  departureTimeFilters?: StringRangeType;
  arrivalTimeFilters?: StringRangeType;
  maxDurationInHours?: number;
  priceFilters?: DecimalRangeType;
  carriers?: string[];
}
